import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiUrl } from '../lib/config';

export interface Character {
  id: number;
  name: string;
  corporationId: number;
  corporationName?: string;
  allianceId?: number;
  allianceName?: string;
}

interface AuthState {
  character: Character | null;
  isAuthenticated: boolean;
  isWhitelisted: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  setCharacter: (character: Character | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  logout: () => void;
  checkAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      character: null,
      isAuthenticated: false,
      isWhitelisted: false,
      isAdmin: false,
      isLoading: true,
      error: null,

      setCharacter: (character) =>
        set({
          character,
          isAuthenticated: !!character,
          isLoading: false,
          error: null,
        }),

      setLoading: (isLoading) => set({ isLoading }),

      setError: (error) => set({ error, isLoading: false }),

      logout: async () => {
        try {
          await fetch(apiUrl('/api/auth/logout'), { method: 'POST', credentials: 'include' });
        } catch (e) {
          console.error('Logout failed:', e);
        }
        set({
          character: null,
          isAuthenticated: false,
          isWhitelisted: false,
          isAdmin: false,
          isLoading: false,
          error: null,
        });
      },

      checkAuth: async () => {
        set({ isLoading: true });
        try {
          const response = await fetch(apiUrl('/api/auth/me'), { credentials: 'include' });
          if (response.ok) {
            const data = await response.json();
            set({
              character: data.character,
              isAuthenticated: !!data.character,
              isWhitelisted: data.isWhitelisted || false,
              isAdmin: data.isAdmin || false,
              isLoading: false,
              error: null,
            });
          } else {
            set({
              character: null,
              isAuthenticated: false,
              isWhitelisted: false,
              isAdmin: false,
              isLoading: false,
              error: null,
            });
          }
        } catch (error) {
          set({
            character: null,
            isAuthenticated: false,
            isWhitelisted: false,
            isAdmin: false,
            isLoading: false,
            error: 'Failed to check authentication',
          });
        }
      },
    }),
    {
      name: 'wh-mapper-auth',
      partialize: (state) => ({ character: state.character }),
    }
  )
);
